import { headers } from "./ApiHelpers";

const baseUrl = "/api/schedules";

export type ScheduleDay = "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY";

export type ClassroomScheduleEntry = {
  id: number;
  classId: string;
  day: ScheduleDay;
  /**
   * API returns HHmm in 24 hour format
   */
  startTime: string;
  /**
   * API returns HHmm in 24 hour format
   */
  endTime: string;
  /**
   * API returns DD/MM/YYYY format
   */
  endDate: string;
  paused: boolean;
  modifiedBy: string;
  modifiedDate: string;
};

export type GetClassroomSchedulesResponse = {
  schedules: ClassroomScheduleEntry[];
};

/**
 * Gets the (new) classroom schedules for a given classroom
 */
export const getClassroomSchedules = async (classroomStableId: string): Promise<GetClassroomSchedulesResponse> => {
  const raw = await fetch(`${baseUrl}/classrooms/${classroomStableId}`, {
    headers: headers(),
  });
  return (await raw.json()) as GetClassroomSchedulesResponse;
};

export type ImportClassroomSchedulesRequest = {
  schedules: ScheduleRule[];
};

export type ScheduleRule = {
  className: string;
  username: string;
  day: ScheduleDay;
  /**
   * API expects HHmm in 24 hour format
   */
  startTime: string;
  /**
   * API expects HHmm in 24 hour format
   */
  endTime: string;
  /**
   * API expects DD/MM/YYYY format
   */
  endDate: string;
};

export type ImportClassroomSchedulesResponse = {
  schedulesImported: number;
  recurrenceRulesImported: number;
};

export type ImportClassroomSchedulesErrorResponse = {
  errors: ImportClassroomSchedulesError[];
  message: string;
};

export type ImportClassroomSchedulesError = {
  className: string;
  username: string;
  error: string;
};

class ImportSchedulesValidationError extends Error {
  validationErrors: ImportClassroomSchedulesError[];
  constructor(message: string, validationErrors: ImportClassroomSchedulesError[]) {
    super(message);
    this.name = "ImportSchedulesValidationError";
    this.validationErrors = validationErrors;
  }
}

/**
 * Imports classroom schedules
 */
export const importClassroomSchedules = async (schedules: ScheduleRule[]): Promise<ImportClassroomSchedulesResponse> => {
  const request: ImportClassroomSchedulesRequest = {
    schedules,
  };
  const response = await fetch(`${baseUrl}/import`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify(request),
  });
  if (response.ok) {
    return (await response.json()) as ImportClassroomSchedulesResponse;
  }

  if (response.status === 400) {
    const responseBody = (await response.json()) as unknown;
    if (isImportClassroomSchedulesErrorResponse(responseBody)) {
      // This can even occur if any of the field formats are invalid (i.e time is formatted as HHMM not HH:MM, etc)
      throw new ImportSchedulesValidationError("Validation error", responseBody.errors);
    }
    throw new Error(`Invalid request payload, error code ${response.status}`);
  }

  throw new Error(`Invalid response from server, unexpected error code ${response.status}`);
};

export const isImportClassroomSchedulesErrorResponse = (response: unknown): response is ImportClassroomSchedulesErrorResponse => {
  return typeof response === "object" && response !== null && "errors" in response;
};

export function isImportSchedulesValidationError(error: unknown): error is ImportSchedulesValidationError {
  return error instanceof Error && error.name === "ImportSchedulesValidationError";
}
